/**
 * Profile dropdown component — renders in the global toolbar.
 * Shows user avatar, name, and dropdown with Profile / Settings / Sign Out.
 */
import { router, store, auth, getIcon } from '@isg/ui-kit';
import { t } from '../i18n/index.js';
import { isKioskSession } from '../kiosk.js';

/** A role slug as words: a known role has a key; an unknown one is humanised. */
function roleLabel(role) {
  const key = `profile_menu.roles.${role}`;
  const label = t(`profile_menu.roles.${role}`);
  // i18n-ignore: a role the catalogue does not know yet, shown humanised
  return label === key ? String(role).replace(/_/g, ' ') : label;
}

/**
 * Create the profile dropdown element.
 * @returns {HTMLElement}
 */
export function createProfileDropdown() {
  const user = store.get('user') || {};
  const displayName = user.display_name || user.username || t('profile_menu.user');
  const initial = displayName.charAt(0).toUpperCase();
  // 🔴 BETA-00216: this rendered an initial and NOTHING else — the toolbar
  // avatar beside your name never showed a photo, for anyone, ever. Andy
  // Jenkins uploaded one and saw it only on the profile page and the
  // directory, which are the two surfaces that resolve it.
  const face = (cls) => (user.avatar_url
    ? `<div class="${cls}"><img src="${escHtml(user.avatar_url)}" alt=""></div>`
    : `<div class="${cls}">${initial}</div>`);
  const role = user.role || 'user';

  const wrapper = document.createElement('div');
  wrapper.className = 'profile-dropdown';

  wrapper.innerHTML = `
    <button class="profile-dropdown-trigger" id="profile-trigger" aria-label="${t('profile_menu.aria')}" aria-expanded="false">
      ${face('profile-avatar-sm')}
      <span class="profile-name">${escHtml(displayName)}</span>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="6 9 12 15 18 9"/>
      </svg>
    </button>
    <div class="profile-dropdown-menu" id="profile-menu">
      <div class="profile-dropdown-header">
        ${face('profile-avatar-lg')}
        <div>
          <div class="profile-dropdown-name">${escHtml(displayName)}</div>
          <div class="profile-dropdown-role">${escHtml(roleLabel(role))}</div>
        </div>
      </div>
      <div class="dropdown-divider"></div>
      <a href="/profile" data-link class="dropdown-item profile-dropdown-item" data-path="/profile">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
        </svg>
        ${t('profile_menu.my_profile')}
      </a>
      <a href="/settings" data-link class="dropdown-item profile-dropdown-item" data-path="/settings">
        ${getIcon('settings')}
        ${t('nav.settings')}
      </a>
      <div class="dropdown-divider"></div>
      ${isKioskSession() ? `
        <div class="kiosk-signout-hint">
          ${getIcon('credit-card', 16)}
          <span>${t('kiosk.signOutHint')}</span>
        </div>` : `
        <button class="dropdown-item profile-dropdown-item profile-dropdown-logout" id="profile-logout">
          ${getIcon('log-out')}
          ${t('profile_menu.sign_out')}
        </button>`}
    </div>
  `;

  const trigger = wrapper.querySelector('#profile-trigger');
  const menu = wrapper.querySelector('#profile-menu');
  let isOpen = false;

  function toggle() {
    isOpen = !isOpen;
    menu.classList.toggle('active', isOpen);
    trigger.setAttribute('aria-expanded', String(isOpen));
  }

  function close() {
    isOpen = false;
    menu.classList.remove('active');
    trigger.setAttribute('aria-expanded', 'false');
  }

  trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    toggle();
  });

  document.addEventListener('click', (e) => {
    if (isOpen && !wrapper.contains(e.target)) {
      close();
    }
  });

  // Navigation items
  menu.querySelectorAll('[data-path]').forEach((item) => {
    item.addEventListener('click', (e) => {
      e.preventDefault();
      close();
      router.navigate(item.dataset.path);
    });
  });

  // Logout
  // On a kiosk there is no Sign out button — taking the card out is how you
  // leave, so the element is absent rather than disabled.
  wrapper.querySelector('#profile-logout')?.addEventListener('click', () => {
    close();
    auth.logout();
  });

  return wrapper;
}

function escHtml(str) {
  const d = document.createElement('div');
  d.textContent = str || '';
  return d.innerHTML;
}
